import currencyLib from "currency.js";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../../../services/hooks/useTranslationI18";
import {
  removeOfferAction,
  setRedirectItem,
  setSelectedBuySubItem,
  setSelectedOfferId,
  setSelectedOfferSubItems,
} from "../../../../../services/redux/slices/cartLoyaltyV2Slice";
import { CartItemType } from "../../../../../services/redux/types/CartLoyaltyV2Types";
import { MAIN_OFFER_TYPE } from "../../../../../utils/enums";
import { DoubleLinkedList } from "../../../../../utils/help/linkedList/LinkedList";
import {
  isArabicLanguage,
  localizedNumber,
} from "../../../../../utils/help/localization/localization";
import { capitalizeFirstLetter } from "../../../../../utils/help/textPreparation/textPreparation";
import { ItemOffer } from "../../../../../utils/OfferClass/ItemOffer/ItemOffer";
import Offer from "../../../../../utils/OfferClass/Offer/Offer";
import { INVERTED_SECTIONS } from "../../../../../utils/types";
import { DeleteRedIcon, EditIcon } from "../../Cart.icons";
import styles from "../ItemList.module.scss";
import { removeSpaces } from "../ItemListComponentHelper";
import { BundleOfferImages } from "./bundleItemImage";

export const BundleItemCard = () => {
  const isLanguageArabic = isArabicLanguage();
  const { t } = useTranslationI18();
  const dispatch = useAppDispatch();

  const currency = useAppSelector((state) => state.config.currency);

  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const isInvertedItemCart = restaurantInfo?.colors?.invertedSections?.includes(
    INVERTED_SECTIONS.ITEM_CART
  );

  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const editOrder = t("cart:order-edit");
  const deleteOrder = t("cart:cart-delete");

  const cartV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);

  const offer = cartAndLoyaltyReducer.offersArray.find((offer) =>
    offer.isEqual(cartV2.cart.redeemedOffer)
  );

  const redeemedOffer = cartV2.cart.redeemedOffer;
  const subText = Offer.getLocalizedValue(
    redeemedOffer?.uiDetails?.cartCard?.subText
  );

  const priceWithoutOffer =
    offer?.uiDetails?.cartCard.priceWithoutOffer ||
    redeemedOffer?.uiDetails?.cartCard.priceWithoutOffer;

  const offerPrice =
    redeemedOffer?.uiDetails?.cartCard?.price ||
    (offer instanceof ItemOffer ? offer?.details.price : undefined);

  const selectedBuyItem = cartV2.cart.items.find((item) => item.isBuyItem);
  const selectedOfferItems = cartV2.cart.items.filter(
    (item) => item.isOfferItem || item.isBundleItem
  );

  const handleEdit = () => {
    const selectedOfferSubItems = new DoubleLinkedList<CartItemType>();

    // if (selectedOfferItems.length) {
    if (offer?.isBundleOffer()) {
      selectedOfferItems
        .sort((a, b) => b.totalPrice - a.totalPrice)
        .flatMap((bundleSubitem) => {
          if (bundleSubitem.count > 1) {
            for (let i = 0; i < bundleSubitem.count; i++) {
              selectedOfferSubItems.append({
                ...bundleSubitem,
                count: 1,
                priceWithoutOffer: currencyLib(
                  bundleSubitem.priceWithoutOffer!
                ).divide(bundleSubitem.count).value,
              });
            }
          } else {
            selectedOfferSubItems.append(bundleSubitem);
          }

          return [];
        });
    } else if (offer?.isBogoOffer()) {
      selectedOfferItems.forEach((item) => {
        selectedOfferSubItems.append(item);
      });
    }
    // }

    dispatch(setSelectedOfferSubItems(selectedOfferSubItems));
    dispatch(setSelectedOfferId(offer?.id!));

    if (offer?.isBogoOffer()) {
      dispatch(setSelectedBuySubItem(selectedBuyItem));

      dispatch(
        setRedirectItem({
          itemId: selectedBuyItem?.itemDetails.id,
          subItemId: selectedBuyItem?.subItemDetails.id,
        })
      );

      return;
    }

    const head = selectedOfferSubItems.getHead();
    if (!head) {
      return;
    }
    dispatch(setSelectedOfferId(offer?.id!));
    dispatch(
      setRedirectItem({
        itemId: head.subItemDetails.itemId!,
        subItemId: head.subItemDetails.id,
      })
    );
  };

  const handleDelete = () => {
    dispatch(removeOfferAction());
  };

  const addonsPrice = () => {
    const bundleItems = cartV2.cart.items.filter((item) => item.isBundleItem);
    if (bundleItems.length === 0) {
      return 0;
    }
    const bundleItemsTotal = bundleItems.reduce((total, item) => {
      return total + currencyLib(item.totalPrice).value;
    }, 0);

    return offer?.details && "price" in offer.details
      ? Math.floor(
          currencyLib(Math.round(bundleItemsTotal)).subtract(
            offer?.details.price
          ).value
        )
      : 0;
  };

  const createMarkup = (html: string) => {
    return { __html: html };
  };

  const cardTitle =
    Offer.getLocalizedValue(offer?.uiDetails?.cartCard.label) ||
    Offer.getLocalizedValue(offer?.uiDetails?.mainText);

  return (
    <div
      className={` ${styles.card} ${styles.bundle_background_color} ${
        !cartV2.cart.isEligibleOffer && styles.dimmed_card
      } bg__zero ${isInvertedItemCart && "invert"}`}
      data-testid={`cart-item-${offer?.uiDetails?.mainText.en}`}
      key={`${removeSpaces(offer?.offerType ?? "")}`}
    >
      <div className={`${styles.card_details} ${styles.width_100}`}>
        <BundleOfferImages
          urls={offer?.uiDetails?.graphic?.value}
          graphics={
            redeemedOffer?.offerType === MAIN_OFFER_TYPE.BOGO
              ? redeemedOffer.uiDetails?.graphics
              : undefined
          }
        />
        <div className={`${styles.card_content} ${styles.width_100}`}>
          <div className={styles.card_body}>
            <div
              className={styles.name}
              data-testid={`cart-item-${offer?.details}`}
            >
              <span>{`${capitalizeFirstLetter(cardTitle || "")}`}</span>
            </div>
            <p className={styles.add_on_container}>
              <span
                className={`${styles.add_on_container__addons} ${styles.bundle_addons}`}
              >
                <span
                  className={styles.bundle_items}
                  data-testid={`bundle-items`}
                  dangerouslySetInnerHTML={createMarkup(
                    subText ||
                      Offer.getLocalizedValue(
                        offer?.uiDetails?.cartCard?.subText
                      ) ||
                      ""
                  )}
                ></span>
                {addonsPrice() > 0 && (
                  <span
                    className={styles.add_on_container__price}
                    data-testid={`${offer?.offerType}-addon-price`}
                  >
                    {isLanguageArabic
                      ? `+${localizedNumber(addonsPrice())} ${currency}`
                      : `+${currency}${localizedNumber(addonsPrice())}`}
                  </span>
                )}
              </span>
            </p>

            {
              <p
                className={styles.price}
                data-testid={`${offer?.offerType}-cart-prices`}
              >
                {priceWithoutOffer && (
                  <span className={styles.crossed_price}>
                    {isLanguageArabic
                      ? `${localizedNumber(
                          currencyLib(priceWithoutOffer).value
                        )} ${currency}`
                      : `${currency}${localizedNumber(
                          currencyLib(priceWithoutOffer).value
                        )}`}
                  </span>
                )}
                {offerPrice &&
                  (isLanguageArabic
                    ? `${localizedNumber(
                        currencyLib(offerPrice).value
                      )} ${currency}`
                    : `${currency}${localizedNumber(
                        currencyLib(offerPrice).value
                      )}`)}
              </p>
            }

            <div className={styles.item_actions}>
              <a onClick={handleEdit}>
                <div className={styles.edit}>
                  <EditIcon />
                  <p data-testid={`edit-button-${offer?.offerType}`}>
                    {editOrder}
                  </p>
                </div>
              </a>

              <div
                className={styles.delete}
                data-testid={`delete-icon-${offer?.offerType}`}
              >
                <DeleteRedIcon />
                <p onClick={handleDelete}>{deleteOrder}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
